import axios from '../utils/axios';

/** The signed-in user's stored digital signature and professional registration. */
export interface UserSignature {
  hasSignature: boolean;
  /** `data:image/jpeg;base64,...` */
  image?: string;
  name?: string;
  updatedAt?: string;
  /** GOC performer list number, defaulted onto GOS claims this user submits. */
  gocNumber?: string;
}

/** Staff offered as the practitioner of an appointment. */
export interface PractitionerSummary {
  id: number;
  firstName?: string;
  lastName?: string;
  roleId?: string;
}

export class UsersService {
  createUser(data: any) {
    return axios.post('/users', data);
  }
  getAllUsers(params?: Record<string, any>) {
    return axios.get('/users', { params });
  }
  /**
   * Active practitioners (professional + clinician) of the signed-in user's
   * organisation. Unlike `GET /users` this is not admin-only, so receptionists
   * and clinicians can populate practitioner pickers and filters too — with
   * `GET /users` they got a swallowed 403 and an empty list.
   */
  getPractitioners(config?: Record<string, any>) {
    // Untyped response: callers read either the array or a `{ data }` wrapper.
    return axios.get('/users/practitioners', config);
  }
  updateUser(data: any, id?: string) {
    return axios.patch(`/users${id ? `?userId=${id}` : ''}`, data);
  }
  deleteUser(id?: string) {
    return axios.delete(`/users/${id}`);
  }
  /** Deactivate (no sign-in, hidden from pickers) or reactivate a user. */
  setUserStatus(id: string | number, isActive: boolean) {
    return axios.patch(`/users/${id}/status`, { isActive });
  }

  /** The signed-in user's own name and colour. */
  updateMyProfile(payload: {
    firstName: string;
    lastName?: string;
    profileColor?: string | null;
  }) {
    return axios.patch('/users/me/profile', payload);
  }

  /** Change own password; the current password must be supplied. */
  changeMyPassword(payload: { currentPassword: string; newPassword: string }) {
    return axios.put<{ message: string }>('/users/me/password', payload);
  }

  // ── Digital signature ─────────────────────────────────────────────────────
  //
  // Scoped to the signed-in user with no id override, matching the backend: a
  // signature auto-attached in someone's name must only ever be set by them.

  getMySignature() {
    return axios.get<UserSignature>('/users/me/signature');
  }

  /** `image` must be a JPEG data URL — NHS eGOS rejects any other format. */
  setMySignature(payload: {
    image: string;
    name?: string;
    gocNumber?: string;
  }) {
    return axios.put<UserSignature>('/users/me/signature', payload);
  }

  /** Record the GOC performer list number without touching the signature. */
  setMyGocNumber(gocNumber: string) {
    return axios.put<UserSignature>('/users/me/goc-number', { gocNumber });
  }

  clearMySignature() {
    return axios.delete<UserSignature>('/users/me/signature');
  }
}
